import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Chip
} from '@mui/material';
import AnalyticsService from '../../services/AnalyticsService';

interface PageData {
  page: string;
  views: number;
  avgDuration: number;
  bounceRate: number;
}

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f', '#0288d1', '#388e3c', '#f57c00'];

const GamePerformanceChart: React.FC = () => {
  const [data, setData] = useState<PageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalViews, setTotalViews] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AnalyticsService.fetchPagePerformance();

      // Transform and filter the data for game-related pages
      const pageData: PageData[] = response.data.rows
        .filter(row => {
          // Filter for game-related pages
          const path = row.pagePath;
          return path.includes('/game') || path === '/' || path.includes('/play');
        })
        .map(row => {
          // Clean up page names for display
          let pageName = row.pagePath;
          if (pageName === '/') {
            pageName = 'Home';
          } else if (pageName.includes('/game')) {
            pageName = 'Game';
          } else if (pageName.includes('/play')) {
            pageName = 'Play';
          } else {
            pageName = pageName.replace(/^\//, '').replace(/\//g, ' > ');
          }

          return {
            page: pageName,
            views: parseInt(row.screenPageViews || 0),
            avgDuration: parseFloat(row.averageSessionDuration || 0),
            bounceRate: parseFloat(row.bounceRate || 0) * 100
          };
        })
        .sort((a, b) => b.views - a.views)
        .slice(0, 8); // Top 8 pages

      const total = pageData.reduce((sum, item) => sum + item.views, 0);
      setTotalViews(total);
      setData(pageData);
    } catch (err) {
      console.error('Error fetching page performance:', err);
      setError('Failed to load page performance data');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload[0]) {
      const data = payload[0].payload;
      return (
        <Box sx={{ bgcolor: 'background.paper', p: 1, border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
          <Typography variant="subtitle2">{data.page}</Typography>
          <Typography variant="body2">Views: {data.views.toLocaleString()}</Typography>
          <Typography variant="body2">Avg Duration: {formatDuration(data.avgDuration)}</Typography>
          <Typography variant="body2">Bounce Rate: {data.bounceRate.toFixed(1)}%</Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 3, height: '400px', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2">
          Game Page Performance
        </Typography>
        {!loading && !error && data.length > 0 && (
          <Chip
            label={`Total Views: ${totalViews.toLocaleString()}`}
            color="primary"
            variant="outlined"
          />
        )}
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {!loading && !error && data.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="page" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="views" name="Page Views" fill="#1976d2">
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}

        {!loading && !error && data.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">No data available</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default GamePerformanceChart;